import React, { useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import { Tooltip } from '@mui/material';
import { createPortal } from 'react-dom';
import { PlusIcon, MagnifyingGlassIcon, QuestionMarkCircleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import InputBox from '../FormComponent/InputBox';
import ButtonComponent from '../customButton';
import FilterPill from './FilterPill';
import ModeToggle from './ModeToggle';
import AddFilterMenu from './AddFilterMenu';
import QuerySyntaxHelpModal from './QuerySyntaxHelpModal';
import { useQueryAutocomplete } from '../../hooks/useQueryAutocomplete';
import styles from '../../styles/query/FilterBar.module.scss';

/**
 * Filter Bar Component
 * 
 * Container for filter pills, quick filter, and mode toggle
 * Compact JIRA-like design with all elements in one line
 * 
 * @param {Array} filterPills - Array of filter pills
 * @param {string} quickFilter - Quick filter text
 * @param {string} inputText - Advanced query text (for advanced mode)
 * @param {Object} schema - Query schema
 * @param {string} mode - Current mode: 'ui' | 'advanced'
 * @param {boolean} isComplex - Whether the query is too complex for UI mode
 * @param {Object} parseError - Parse error object
 * @param {boolean} loading - Loading state
 * @param {boolean} isParsing - Parsing state
 * @param {function} onFilterChange - Handler for filter changes
 * @param {function} onFilterApply - Handler for filter apply (triggers query execution)
 * @param {function} onQuickFilterChange - Handler for quick filter changes
 * @param {function} onTextChange - Handler for advanced query text change
 * @param {function} onExecute - Handler for query execution
 * @param {function} [onClear] - Handler for clear (clear text + apply empty filter). If provided, used instead of onTextChange('') + onExecute()
 * @param {function} onAddFilter - Handler to add new filter
 * @param {function} onModeChange - Handler for mode change
 * @param {function} onClearAll - Handler to clear all filters
 */
export function FilterBar({
  filterPills = [],
  quickFilter = '',
  inputText = '',
  schema,
  mode = 'ui',
  isComplex = false,
  parseError = null,
  loading = false,
  isParsing = false,
  onFilterChange,
  onFilterApply,
  onQuickFilterChange,
  onTextChange,
  onExecute,
  onClear,
  onAddFilter,
  onModeChange,
  onClearAll
}) {
  const isAdvanced = mode === 'advanced';
  
  const cursorPositionRef = useRef(0);
  const [cursorPosition, setCursorPosition] = useState(0);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [isInputFocused, setIsInputFocused] = useState(false);
  const [showAddFilterMenu, setShowAddFilterMenu] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const addFilterButtonRef = useRef(null);
  const inputRef = useRef(null);
  const anchorElRef = useRef(null);
  const suggestionsListRef = useRef(null);
  const suggestionsPopperRef = useRef(null);
  const isMouseOverSuggestionsRef = useRef(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  
  const updateCursorPosition = useCallback((position, inputElement = null) => {
    cursorPositionRef.current = position;
    setCursorPosition(position);
    if (inputElement) {
      anchorElRef.current = inputElement;
    }
  }, []);
  
  const { suggestions, context } = useQueryAutocomplete(
    isAdvanced ? inputText : '', 
    cursorPosition, 
    'testcase',
    schema
  );
  
  const calculateDropdownPosition = useCallback(() => {
    const container = inputRef.current;
    if (!container || !document.body.contains(container)) {
      // Reset position if container is invalid
      setDropdownPosition({ top: 0, left: 0, width: 0 });
      return;
    }
    
    // Find the actual input element inside the container
    const inputElement = container.querySelector('input, textarea');
    if (!inputElement || !document.body.contains(inputElement)) {
      setDropdownPosition({ top: 0, left: 0, width: 0 });
      return;
    }
    
    // Use the input element's bounding rect for precise positioning
    const rect = inputElement.getBoundingClientRect();
    
    // Validate rect is valid and has reasonable dimensions
    if (rect.width === 0 && rect.height === 0) {
      setDropdownPosition({ top: 0, left: 0, width: 0 });
      return;
    }
    
    // Additional validation: ensure input is actually visible on screen
    // If top/left are both 0 and width is 0, it's likely not positioned yet
    if (rect.top === 0 && rect.left === 0 && rect.width < 100) {
      // Might be initial render before layout - don't position yet
      return;
    }
    
    const actualDropdownHeight = suggestionsListRef.current?.offsetHeight || 300;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const width = Math.max(rect.width, 300);
    
    // Always align to the left edge of the input
    let left = rect.left;
    
    // If dropdown would overflow right edge, shift left
    if (left + width > viewportWidth) {
      left = Math.max(8, viewportWidth - width);
    }
    
    // Ensure dropdown doesn't go off the left edge
    if (left < 0) {
      left = 8;
    }
    
    // Position directly below the input with a small gap
    let top = rect.bottom + 4;
    
    // Check if dropdown would overflow bottom of viewport
    const wouldOverflowBottom = top + actualDropdownHeight > viewportHeight;
    
    // If it would overflow, try positioning above the input
    if (wouldOverflowBottom) {
      const topPositionAbove = rect.top - actualDropdownHeight - 4;
      
      // Only position above if there's actually reasonable space above (at least 8px from top)
      // If not enough space above, keep it below the input (user can scroll to see it)
      if (topPositionAbove >= 8) {
        top = topPositionAbove;
      }
      // Otherwise, keep top = rect.bottom + 4 (below input)
      // This ensures dropdown always appears relative to the input, not at arbitrary 8px
    }
    
    // Final validation: top should always be relative to the input position
    // If top is less than rect.bottom, something went wrong (we should always be at or below input)
    if (top < rect.bottom) {
      // This shouldn't happen, but if it does, ensure we're at least below the input
      top = rect.bottom + 4;
    }
    
    // Ensure top is not negative
    if (top < 0) {
      top = rect.bottom + 4;
    }
    
    // Final sanity check: if calculated position seems completely wrong, don't update
    // (e.g., if input is at y=500 but dropdown would be at y=0, something's wrong)
    if (Math.abs(top - rect.bottom) > 1000 && rect.bottom > 100) {
      // Position calculation seems way off - don't update
      return;
    }
    
    setDropdownPosition({
      top,
      left,
      width
    });
  }, []);

  // Recalculate position SYNCHRONOUSLY before paint (useLayoutEffect)
  // whenever suggestions visibility, input text, or cursor position changes.
  useLayoutEffect(() => {
    if (!showSuggestions || !inputRef.current) return;
    
    // Ensure input element exists and is in DOM
    const inputElement = inputRef.current.querySelector('input, textarea');
    if (!inputElement || !document.body.contains(inputElement)) return;
    
    // Double-check the element is actually visible (has dimensions)
    const rect = inputElement.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return;
    
    calculateDropdownPosition();
  }, [calculateDropdownPosition, showSuggestions, inputText, cursorPosition]);

  // Recalculate on scroll / resize so the dropdown follows the input
  useEffect(() => {
    if (!showSuggestions) return;

    let rafId = null;
    
    const updatePosition = () => {
      // Cancel any pending RAF to debounce rapid scroll/resize events
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
      
      // Use RAF for scroll/resize events to ensure layout has updated
      // This is different from useLayoutEffect - here we're responding to async events
      rafId = requestAnimationFrame(() => {
        calculateDropdownPosition();
        rafId = null;
      });
    };

    // Listen to scroll on window and document (capture phase to catch all scrolls)
    // Also listen to resize for viewport changes
    window.addEventListener('scroll', updatePosition, true);
    document.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);

    return () => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
      window.removeEventListener('scroll', updatePosition, true);
      document.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [showSuggestions, calculateDropdownPosition]);
  
  useEffect(() => {
    if (isAdvanced && suggestions.length > 0 && !parseError && isInputFocused) {
      setShowSuggestions(true);
      setSelectedIndex(-1);
      setTimeout(() => {
        if (inputRef.current) {
          calculateDropdownPosition();
        }
      }, 0);
    } else {
      setShowSuggestions(false);
    }
  }, [suggestions.length, parseError, isAdvanced, isInputFocused, calculateDropdownPosition]);
  
  // Scroll selected item into view
  useEffect(() => {
    if (showSuggestions && suggestionsListRef.current && selectedIndex >= 0) {
      const selectedItem = suggestionsListRef.current.children[selectedIndex];
      if (selectedItem) {
        selectedItem.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }
  }, [selectedIndex, showSuggestions]);
  
  // Helper: Escape value for double quotes
  const escapeForDoubleQuotes = useCallback((value) => {
    return String(value).replace(/"/g, '\\"');
  }, []);

  // Helper: Escape value for single quotes
  const escapeForSingleQuotes = useCallback((value) => {
    return String(value).replace(/'/g, "\\'");
  }, []);

  // Helper: Get quoted value with appropriate escaping
  const getQuotedValue = useCallback((value, quoteChar = '"') => {
    const escaped = quoteChar === '"' 
      ? escapeForDoubleQuotes(value) 
      : escapeForSingleQuotes(value);
    return `${quoteChar}${escaped}${quoteChar}`;
  }, [escapeForDoubleQuotes, escapeForSingleQuotes]);

  // Insert field or operator suggestion
  const insertFieldSuggestion = useCallback((suggestion, textBefore, cursorStart) => {
    const wordMatch = textBefore.match(/(\w+)$/);
    const tokenStart = wordMatch ? cursorStart - wordMatch[1].length : cursorStart;
    const value = suggestion?.value ?? '';
    const insertText = value ? `${value} ` : ' ';
    
    return { tokenStart, insertText };
  }, []);

  // Insert logical operator (AND, OR, NOT) with proper spacing
  const insertLogicalOperator = useCallback((suggestion, textBefore, cursorStart) => {
    const wordMatch = textBefore.match(/(\w+)$/);
    const tokenStart = wordMatch ? cursorStart - wordMatch[1].length : cursorStart;
    const beforeToken = textBefore.substring(0, tokenStart);
    const needsSpaceBefore = beforeToken.length > 0 && !/\s$/.test(beforeToken);
    const value = suggestion?.value ?? '';
    const insertText = value ? `${needsSpaceBefore ? ' ' : ''}${value} ` : ' ';
    
    return { tokenStart, insertText };
  }, []);

  // Handle value insertion for 'in' or 'notIn' operators
  const handleInOperatorValue = useCallback((
    suggestion,
    textBefore,
    actualInputValue,
    cursorStart,
    getQuotedValue
  ) => {
    const value = suggestion?.value ?? '';
    if (!value) {
      // Return empty insertion if no value
      return { tokenStart: cursorStart, insertText: '' };
    }
    
    const beforeCursor = textBefore.trim();
    const isInBrackets = beforeCursor.endsWith('[') || /\[[^\]]*$/.test(beforeCursor);
    const lastBracketIndex = textBefore.lastIndexOf('[');
    const lastCommaIndex = textBefore.lastIndexOf(',');
    const quotedValue = getQuotedValue(value);
    
    if (isInBrackets && lastBracketIndex >= 0) {
      // Inside brackets - handle various cases
      const textAfterBracketInActual = actualInputValue.substring(lastBracketIndex + 1, cursorStart);
      const incompleteQuoteMatch = textAfterBracketInActual.match(/(["'])([^"']*)$/);
      
      if (incompleteQuoteMatch) {
        // Inside quotes within brackets
        const quoteChar = incompleteQuoteMatch[1];
        const quoteIndexInBracket = textAfterBracketInActual.indexOf(quoteChar);
        const quoteStart = lastBracketIndex + 1 + quoteIndexInBracket;
        
        if (actualInputValue.charAt(quoteStart) === quoteChar) {
          const valueToInsert = quoteChar === '"' 
            ? escapeForDoubleQuotes(value) 
            : escapeForSingleQuotes(value);
          
          // Start AFTER the opening quote, just replace the content + add closing quote
          return {
            tokenStart: quoteStart + 1,  // After the opening quote
            insertText: valueToInsert + quoteChar  // Just value + closing quote
          };
        } else {
          // Fallback
          return {
            tokenStart: lastBracketIndex + 1,
            insertText: quotedValue
          };
        }
      } else if (!beforeCursor.endsWith('[')) {
        // Inside brackets with content, not in quotes
        if (lastCommaIndex > lastBracketIndex) {
          // After comma - skip existing whitespace
          const afterComma = textBefore.substring(lastCommaIndex + 1);
          const trimmedLength = afterComma.length - afterComma.trimStart().length;
          return {
            tokenStart: lastCommaIndex + 1 + trimmedLength,
            insertText: ' ' + quotedValue
          };
        } else {
          // First value in bracket - skip existing whitespace
          const afterBracket = textBefore.substring(lastBracketIndex + 1);
          const trimmedLength = afterBracket.length - afterBracket.trimStart().length;
          return {
            tokenStart: lastBracketIndex + 1 + trimmedLength,
            insertText: quotedValue
          };
        }
      } else {
        // Just opened bracket
        return {
          tokenStart: lastBracketIndex + 1,
          insertText: quotedValue
        };
      }
    } else {
      // Not in brackets - create new bracket list
      const tokenMatch = textBefore.match(/(\w+|\[|\s+)$/);
      const tokenStart = (tokenMatch && /\w+$/.test(tokenMatch[1])) 
        ? cursorStart - tokenMatch[1].length 
        : cursorStart;
      
      return {
        tokenStart,
        insertText: '[' + quotedValue + ']'
      };
    }
  }, [escapeForDoubleQuotes, escapeForSingleQuotes]);

  // Handle value insertion for regular operators (not 'in'/'notIn')
  const handleRegularOperatorValue = useCallback((
    suggestion,
    textBefore,
    cursorStart,
    getQuotedValue
  ) => {
    const value = suggestion?.value ?? '';
    if (!value) {
      // Return empty insertion if no value
      return { tokenStart: cursorStart, insertText: '' };
    }
    
    const quotedValue = getQuotedValue(value);
    const incompleteQuoteMatch = textBefore.match(
      /(\w+)\s+(>=|<=|!=|=|>|<|contains|startsWith|endsWith)\s+(["'])([^"']*)$/i
    );
    
    if (incompleteQuoteMatch) {
      // Inside incomplete quoted string
      const quoteChar = incompleteQuoteMatch[3];
      const quoteStart = textBefore.lastIndexOf(quoteChar);
      
      if (quoteStart >= 0) {
        const valueToInsert = quoteChar === '"' 
          ? escapeForDoubleQuotes(value) 
          : escapeForSingleQuotes(value);
        return {
          tokenStart: quoteStart + 1,
          insertText: valueToInsert + quoteChar
        };
      } else {
        // Fallback
        return {
          tokenStart: cursorStart,
          insertText: quoteChar === '"' ? quotedValue : getQuotedValue(value, quoteChar)
        };
      }
    } else {
      // Not inside quotes - normal handling
      const tokenMatch = textBefore.match(/(\w+|"|'|\s+)$/);
      let tokenStart = cursorStart;
      
      if (tokenMatch) {
        if (/["']$/.test(tokenMatch[1])) {
          const quoteChar = tokenMatch[1];
          const quoteStart = textBefore.lastIndexOf(quoteChar);
          if (quoteStart >= 0) {
            tokenStart = quoteStart + 1;
          }
        } else if (/\w+$/.test(tokenMatch[1])) {
          tokenStart = cursorStart - tokenMatch[1].length;
        }
      }
      
      return {
        tokenStart,
        insertText: quotedValue
      };
    }
  }, [escapeForDoubleQuotes, escapeForSingleQuotes]);

  // Insert value suggestion (delegates to appropriate handler)
  const insertValueSuggestion = useCallback((
    suggestion,
    textBefore,
    actualInputValue,
    cursorStart,
    getQuotedValue
  ) => {
    const operatorMatch = textBefore.match(
      /(\w+)\s+(in|notIn|>=|<=|!=|=|>|<|contains|startsWith|endsWith|isNotEmpty|isEmpty)(\s*)$/i
    );
    const operator = operatorMatch ? operatorMatch[2].toLowerCase() : null;
    
    if (operator === 'in' || operator === 'notin') {
      return handleInOperatorValue(
        suggestion,
        textBefore,
        actualInputValue,
        cursorStart,
        getQuotedValue
      );
    } else {
      return handleRegularOperatorValue(suggestion, textBefore, cursorStart, getQuotedValue);
    }
  }, [handleInOperatorValue, handleRegularOperatorValue]);

  // Main function: Insert suggestion into text
  const insertSuggestion = useCallback((suggestion) => {
    if (!inputRef.current || !isAdvanced) return;
    
    const input = inputRef.current.querySelector('input');
    if (!input) return;
    
    // Use actual input value and cursor position from DOM to avoid state timing issues
    const actualInputValue = input.value ?? inputText ?? '';
    const actualCursorPosition = input.selectionStart ?? cursorPositionRef.current ?? 0;
    
    // Ensure cursorStart is a valid number within bounds
    const cursorStart = Math.max(0, Math.min(actualCursorPosition, actualInputValue.length));
    const textBefore = actualInputValue.substring(0, cursorStart);
    const textAfter = actualInputValue.substring(cursorStart);
    
    let result;
    
    // Route to appropriate handler based on suggestion type
    if (suggestion.type === 'field' || suggestion.type === 'operator') {
      result = insertFieldSuggestion(suggestion, textBefore, cursorStart);
    } else if (suggestion.type === 'logical') {
      result = insertLogicalOperator(suggestion, textBefore, cursorStart);
    } else if (suggestion.type === 'value') {
      result = insertValueSuggestion(
        suggestion,
        textBefore,
        actualInputValue,
        cursorStart,
        getQuotedValue
      );
    } else {
      // Unknown type - fallback to simple insertion
      const fallbackValue = suggestion?.value ?? '';
      result = {
        tokenStart: cursorStart,
        insertText: fallbackValue ? `${fallbackValue} ` : ' '
      };
    }
    
    const { tokenStart, insertText } = result;
    const newText = actualInputValue.substring(0, tokenStart) + insertText + textAfter;
    const newPosition = tokenStart + insertText.length;
    
    cursorPositionRef.current = newPosition;
    
    const shouldHideSuggestions = suggestion.type === 'value';
    
    if (input && document.activeElement !== input) {
      setIsInputFocused(true);
    }
    
    onTextChange(newText);
    if (shouldHideSuggestions) {
      setShowSuggestions(false);
    }
    
    requestAnimationFrame(() => {
      if (input) {
        input.setSelectionRange(newPosition, newPosition);
        input.focus();
        setIsInputFocused(true);
        updateCursorPosition(newPosition, input);
      }
    });
  }, [
    inputText,
    onTextChange,
    isAdvanced,
    updateCursorPosition,
    insertFieldSuggestion,
    insertLogicalOperator,
    insertValueSuggestion,
    getQuotedValue
  ]);
  
  useEffect(() => {
    const handleClickOutside = (event) => {
      const insideInput = inputRef.current?.contains(event.target);
      const insidePopper = suggestionsPopperRef.current?.contains(event.target);
      if (!insideInput && !insidePopper) {
        setShowSuggestions(false);
        setIsInputFocused(false);
      }
    };
    
    if (showSuggestions) {
      document.addEventListener('pointerdown', handleClickOutside);
      document.addEventListener('mousedown', handleClickOutside);
      
      return () => {
        document.removeEventListener('pointerdown', handleClickOutside);
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showSuggestions]);
  
  const handleTextChange = (e) => {
    const newValue = e.target.value;
    const position = e.target.selectionStart || 0;
    updateCursorPosition(position, e.target);
    onTextChange(newValue);
  };
  
  const handleKeyDown = (e) => {
    if (e.target.selectionStart !== undefined) {
      updateCursorPosition(e.target.selectionStart, e.target);
    }
    
    if (showSuggestions && suggestions.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex(prev => Math.min(prev + 1, suggestions.length - 1));
        return;
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex(prev => Math.max(prev - 1, 0));
        return;
      } else if (e.key === 'Enter' && selectedIndex >= 0 && selectedIndex < suggestions.length) {
        e.preventDefault();
        insertSuggestion(suggestions[selectedIndex]);
        return;
      } else if (e.key === 'Escape') {
        e.preventDefault();
        setShowSuggestions(false);
        return;
      } else if (e.key === 'Tab' && selectedIndex >= 0 && selectedIndex < suggestions.length) {
        e.preventDefault();
        insertSuggestion(suggestions[selectedIndex]);
        return;
      }
    }
    
    if (e.key === 'Enter') {
      e.preventDefault();
      setShowSuggestions(false);
      onExecute();
    }
  };
  
  return (
    <div className={styles.filterBar}>
      <div className={`${styles.filterBarContent} ${isAdvanced && parseError ? styles.hasError : ''}`}>
        {/* Single wrapping row: All elements flow together */}
        <div className={styles.filterBarRow}>
          {/* Mode Toggle */}
          <div className={styles.modeToggleWrapper}>
            <ModeToggle 
              mode={mode} 
              isComplex={isComplex}
              onModeChange={onModeChange}
            />
          </div>
          
          {/* Query Input - Quick Filter (UI mode) or Advanced Query (Advanced mode) */}
          {mode === 'ui' ? (
            <>
              <div className={styles.quickFilterInput}>
                <InputBox
                  size="small"
                  placeholder="Search..."
                  value={quickFilter || ''}
                  onChange={(e) => onQuickFilterChange(e.target.value)}
                  // startIcon={<MagnifyingGlassIcon />}
                />
              </div>
              
              {/* Filter Pills - flow with other elements */}
              {Array.isArray(filterPills) && filterPills.map((pill) => (
                <FilterPill
                  key={pill.id}
                  filter={pill}
                  schema={schema}
                  onEdit={(id, updates) => {
                    const updated = (filterPills || []).map(p => 
                      p.id === id ? { ...p, ...updates } : p
                    );
                    onFilterChange(updated);
                  }}
                  onRemove={(id) => {
                    const filtered = (filterPills || []).filter(p => p.id !== id);
                    onFilterChange(filtered);
                    // Trigger query execution when filter is removed
                    // Note: setTimeout is used here to allow React to batch the state update
                    // before triggering the query execution. This ensures the query runs with
                    // the updated filter state. This is different from cursor position timing issues.
                    if (onFilterApply) {
                      setTimeout(() => {
                        onFilterApply();
                      }, 0);
                    }
                  }}
                  onApply={onFilterApply}
                  isFixed={pill.field === 'project_id' || pill.isFixed}
                />
              ))}
              
              {/* Clear All Button - flows with filters */}
              {((filterPills && filterPills.length > 0) || quickFilter) && onClearAll && (
                <div className={styles.clearAllWrapper}>
                  <ButtonComponent
                    variant="outline"
                    size="small"
                    startIcon={<XMarkIcon className="h-4 w-4" />}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (onClearAll) {
                        onClearAll();
                      }
                    }}
                    className={styles.clearAllButton}
                    label="Clear All"
                  />
                </div>
              )}
              
              {/* Add Filter Button - flows with filters */}
              <div ref={addFilterButtonRef} className={styles.addFilterWrapper}>
                <ButtonComponent
                  variant="outline"
                  size="small"
                  startIcon={<PlusIcon className="h-4 w-4" />}
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowAddFilterMenu(!showAddFilterMenu);
                  }}
                  className={styles.addFilterButton}
                  label="More"
                />
              </div>
              
              {/* Add Filter Menu */}
              {showAddFilterMenu && schema && addFilterButtonRef.current && (
                <AddFilterMenu
                  schema={schema}
                  existingFilters={filterPills || []}
                  onSelect={(newFilter) => {
                    // Add the new filter to the list
                    onFilterChange([...(filterPills || []), newFilter]);
                    setShowAddFilterMenu(false);
                  }}
                  onClose={() => {
                    setShowAddFilterMenu(false);
                  }}
                  anchorEl={addFilterButtonRef.current}
                />
              )}
            </>
          ) : (
            <>
              {/* Advanced Query Input */}
              <div 
                ref={inputRef}
                className={styles.queryInputContainer}
              >
                <div className={styles.advancedInputWrapper}>
                  <InputBox
                    size="small"
                    value={inputText || ''}
                    onChange={handleTextChange}
                    onKeyDown={handleKeyDown}
                    onFocus={(e) => {
                      setIsInputFocused(true);
                      const position = e.target.selectionStart || 0;
                      updateCursorPosition(position, e.target);
                    }}
                    onBlur={(e) => {
                      setTimeout(() => {
                        if (isMouseOverSuggestionsRef.current) {
                          // Blur happened while mouse is over the suggestions panel —
                          // user is interacting with the scrollbar (native scrollbar
                          // clicks don't fire DOM mousedown but do cause blur).
                          // Refocus the input so the dropdown stays open.
                          const input = inputRef.current?.querySelector('input, textarea');
                          if (input) {
                            input.focus();
                            setIsInputFocused(true);
                          }
                          return;
                        }
                        const activeElement = document.activeElement;
                        if (!suggestionsListRef.current?.contains(activeElement)) {
                          setIsInputFocused(false);
                        }
                      }, 200);
                    }}
                    onMouseUp={(e) => {
                      const position = e.target.selectionStart || 0;
                      updateCursorPosition(position, e.target);
                    }}
                    onKeyUp={(e) => {
                      const position = e.target.selectionStart || 0;
                      updateCursorPosition(position, e.target);
                    }}
                    onInput={(e) => {
                      // Keep anchor element in sync while typing (especially at first/last position)
                      const position = e.target.selectionStart || 0;
                      updateCursorPosition(position, e.target);
                    }}
                    onInput={(e) => {
                      // Keep anchor element in sync while typing (especially at first/last position)
                      const position = e.target.selectionStart || 0;
                      updateCursorPosition(position, e.target);
                    }}
                    placeholder='Enter query (e.g., status = "New" AND type = "Functional Test")'
                    customClassName={styles.advancedInput}
                  />
                </div>
                {/* Error message positioned absolutely below input */}
                {parseError && (
                  <div className={styles.errorMessage}>
                    {parseError.message || parseError.error || 'Query parsing error'}
                  </div>
                )}
                
                {/* Autocomplete Suggestions Dropdown - only render when position is calculated */}
                {showSuggestions && suggestions.length > 0 && dropdownPosition.width > 0 && createPortal(
                  <div
                    ref={suggestionsPopperRef}
                    className={styles.suggestionsPopper}
                    style={{
                      position: 'fixed',
                      top: `${dropdownPosition.top}px`,
                      left: `${dropdownPosition.left}px`,
                      width: `${Math.max(dropdownPosition.width, 300)}px`,
                      zIndex: 1300
                    }}
                    onMouseEnter={() => { isMouseOverSuggestionsRef.current = true; }}
                    onMouseLeave={() => { isMouseOverSuggestionsRef.current = false; }}
                    onMouseDown={(e) => {
                      // Prevent the input from blurring when clicking suggestion items.
                      e.preventDefault();
                    }}
                  >
                    <div className={styles.suggestionsPaper}>
                      <ul 
                        ref={suggestionsListRef}
                        className={styles.suggestionsList}
                      >
                        {suggestions.map((suggestion, index) => (
                          <li
                            key={`${suggestion.type}-${suggestion.value}-${index}`}
                            className={`${styles.suggestionItem} ${index === selectedIndex ? styles.selected : ''}`}
                            onClick={() => insertSuggestion(suggestion)}
                            onMouseEnter={() => setSelectedIndex(index)}
                          >
                            <div className={styles.suggestionContent}>
                              <span className={styles.suggestionLabel}>
                                {suggestion.label}
                              </span>
                              <span className={styles.suggestionType}>
                                {suggestion.type}
                              </span>
                            </div>
                            {suggestion.description && (
                              <div className={styles.suggestionDescription}>
                                {suggestion.description}
                              </div>
                            )}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>,
                  document.body
                )}
              </div>
              
              {/* Clear query button - only when there is text; clears text and applies empty filter */}
              {inputText.trim() && (
                <Tooltip title="Clear query and show all">
                  <button
                    type="button"
                    className={styles.clearButton}
                    onClick={() => {
                      if (onClear) {
                        onClear();
                      } else {
                        onTextChange?.('');
                        onExecute?.();
                      }
                    }}
                    disabled={loading || isParsing}
                    aria-label="Clear query and show all"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </Tooltip>
              )}
              
              {/* Execute Button */}
              <Tooltip title="Execute query (Enter)">
                <span>
                  <button
                    type="button"
                    className={styles.executeButton}
                    onClick={onExecute}
                    disabled={loading || isParsing || !inputText.trim()}
                    aria-label="Execute query"
                  >
                    <MagnifyingGlassIcon className="h-5 w-5" />
                  </button>
                </span>
              </Tooltip>
              
              {/* Help Button */}
              <Tooltip title="Query syntax help">
                <button
                  type="button"
                  className={styles.helpButton}
                  aria-label="Query syntax help"
                  onClick={() => setShowHelpModal(true)}
                >
                  <QuestionMarkCircleIcon className="h-5 w-5" />
                </button>
              </Tooltip>
            </>
          )}
        </div>
      </div>
      
      {/* Parsing indicator */}
      {isAdvanced && isParsing && !parseError && (
        <div className={styles.parsingIndicator}>
          <div className={styles.parsingAlert}>
            Searching…
          </div>
        </div>
      )}
      
      {/* Query Syntax Help Modal */}
      <QuerySyntaxHelpModal
        open={showHelpModal}
        onClose={() => setShowHelpModal(false)}
      />
    </div>
  );
}

export default FilterBar;
